// =============================================
// TIPOS: AÇÕES DE ENTRADA/SAÍDA DOS NODES
// =============================================
// Arquivo: actions.ts
// Descrição: Define tipos de ações que podem ser executadas antes/depois do node
// =============================================

export type ActionType =
  // Validações
  | 'validate_required'
  | 'validate_format'
  | 'validate_range'

  // Transformações
  | 'transform_uppercase'
  | 'transform_lowercase'
  | 'transform_trim'
  | 'transform_json_parse'
  | 'transform_json_stringify'
  | 'transform_custom'
  | 'transform_remove_html'
  | 'transform_slugify'

  // Armazenamento
  | 'store_variable'
  | 'store_session'
  | 'store_database'

  // Controle de Fluxo
  | 'condition_check'
  | 'delay_execution'
  | 'retry_on_error'

  // Integração
  | 'send_webhook'
  | 'send_event'
  | 'log_debug'

  // Dados
  | 'merge_data'
  | 'extract_field'
  | 'set_default'

  // Banco de Dados (NOVO)
  | 'db_query'
  | 'db_insert'
  | 'db_update'
  | 'db_delete'

  // API (NOVO)
  | 'api_call'
  | 'api_auth'
  | 'api_rate_limit'

  // Notificações (NOVO)
  | 'push_notification'
  | 'sms_notification'
  | 'whatsapp_notification'

  // Segurança (NOVO)
  | 'encrypt_data'
  | 'decrypt_data'
  | 'hash_password'
  | 'verify_signature';

export interface NodeAction {
  id: string;
  type: ActionType;
  label: string;
  enabled: boolean;
  config: Record<string, any>;
}

export interface InputAction extends NodeAction {
  order: number; // Ordem de execução
}

export interface OutputAction extends NodeAction {
  order: number;
  condition?: string; // Condição para executar (opcional)
}

export interface NodeActions {
  input: InputAction[];
  output: OutputAction[];
}

// Configurações específicas por tipo de ação
export interface ValidateRequiredConfig {
  fields: string[];
  errorMessage?: string;
}

export interface ValidateFormatConfig {
  field: string;
  format: 'email' | 'phone' | 'cpf' | 'cnpj' | 'url' | 'custom';
  pattern?: string;
  errorMessage?: string;
}

export interface ValidateRangeConfig {
  field: string;
  min?: number;
  max?: number;
  errorMessage?: string;
}

export interface TransformCustomConfig {
  code: string; // JavaScript code
  inputVariable: string;
  outputVariable: string;
}

export interface StoreVariableConfig {
  variableName: string;
  value: string; // Pode ser um campo ou expressão
  scope: 'flow' | 'session' | 'global';
}

export interface ConditionCheckConfig {
  condition: string; // Expressão JavaScript
  onTrue?: string; // Ação se verdadeiro
  onFalse?: string; // Ação se falso
}

export interface SendWebhookConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  async?: boolean;
}

export interface ExtractFieldConfig {
  sourcePath: string; // JSONPath ou dot notation
  targetVariable: string;
  defaultValue?: any;
}

export interface MergeDataConfig {
  sources: string[]; // Variáveis para fazer merge
  targetVariable: string;
  strategy: 'shallow' | 'deep' | 'override';
}

// Interfaces de configuração para novos tipos
export interface DBQueryConfig {
  query: string;
  parameters?: Record<string, any>;
  targetVariable: string;
}

export interface APICallConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: string;
  auth?: {
    type: 'bearer' | 'basic' | 'apikey';
    token?: string;
  };
  targetVariable: string;
}

export interface PushNotificationConfig {
  title: string;
  body: string;
  userId?: string;
  data?: Record<string, any>;
  priority: 'high' | 'normal' | 'low';
}

// Metadados das ações disponíveis
export interface ActionDefinition {
  type: ActionType;
  label: string;
  description: string;
  category: 'validation' | 'transformation' | 'storage' | 'control' | 'integration' | 'data' | 'database' | 'api' | 'notification' | 'security';
  icon: string;
  defaultConfig: Record<string, any>;
  configSchema: Array<{
    key: string;
    label: string;
    type: 'text' | 'number' | 'select' | 'textarea' | 'checkbox' | 'array';
    options?: Array<{ value: string; label: string }>;
    placeholder?: string;
    required?: boolean;
  }>;
}

// Catálogo de ações disponíveis
export const ACTION_DEFINITIONS: ActionDefinition[] = [
  // ===== VALIDAÇÕES =====
  {
    type: 'validate_required',
    label: 'Validar Campos Obrigatórios',
    description: 'Verifica se campos obrigatórios estão preenchidos',
    category: 'validation',
    icon: 'fa-check-circle',
    defaultConfig: { fields: [], errorMessage: 'Campo obrigatório não preenchido' },
    configSchema: [
      {
        key: 'fields',
        label: 'Campos Obrigatórios',
        type: 'array',
        placeholder: 'nome, email, telefone',
        required: true
      },
      {
        key: 'errorMessage',
        label: 'Mensagem de Erro',
        type: 'text',
        placeholder: 'Campo obrigatório não preenchido'
      }
    ]
  },
  {
    type: 'validate_format',
    label: 'Validar Formato',
    description: 'Valida formato de dados (email, telefone, etc)',
    category: 'validation',
    icon: 'fa-spell-check',
    defaultConfig: { field: '', format: 'email' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        required: true
      },
      {
        key: 'format',
        label: 'Formato',
        type: 'select',
        options: [
          { value: 'email', label: 'Email' },
          { value: 'phone', label: 'Telefone' },
          { value: 'cpf', label: 'CPF' },
          { value: 'cnpj', label: 'CNPJ' },
          { value: 'url', label: 'URL' }
        ],
        required: true
      }
    ]
  },

  // ===== TRANSFORMAÇÕES =====
  {
    type: 'transform_uppercase',
    label: 'Converter para Maiúsculas',
    description: 'Converte texto para letras maiúsculas',
    category: 'transformation',
    icon: 'fa-font',
    defaultConfig: { field: 'message' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        placeholder: 'message',
        required: true
      }
    ]
  },
  {
    type: 'transform_lowercase',
    label: 'Converter para Minúsculas',
    description: 'Converte texto para letras minúsculas',
    category: 'transformation',
    icon: 'fa-font',
    defaultConfig: { field: 'message' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        placeholder: 'message',
        required: true
      }
    ]
  },
  {
    type: 'transform_trim',
    label: 'Remover Espaços',
    description: 'Remove espaços em branco do início e fim',
    category: 'transformation',
    icon: 'fa-compress',
    defaultConfig: { field: 'message' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        placeholder: 'message',
        required: true
      }
    ]
  },
  {
    type: 'transform_custom',
    label: 'Transformação Customizada',
    description: 'Executa código JavaScript personalizado',
    category: 'transformation',
    icon: 'fa-code',
    defaultConfig: { code: '', inputVariable: 'input', outputVariable: 'output' },
    configSchema: [
      {
        key: 'inputVariable',
        label: 'Variável de Entrada',
        type: 'text',
        placeholder: 'input',
        required: true
      },
      {
        key: 'code',
        label: 'Código JavaScript',
        type: 'textarea',
        placeholder: 'return input.toUpperCase();',
        required: true
      },
      {
        key: 'outputVariable',
        label: 'Variável de Saída',
        type: 'text',
        placeholder: 'output',
        required: true
      }
    ]
  },

  // ===== ARMAZENAMENTO =====
  {
    type: 'store_variable',
    label: 'Armazenar em Variável',
    description: 'Salva valor em variável do fluxo',
    category: 'storage',
    icon: 'fa-database',
    defaultConfig: { variableName: '', value: '', scope: 'flow' },
    configSchema: [
      {
        key: 'variableName',
        label: 'Nome da Variável',
        type: 'text',
        placeholder: 'minha_variavel',
        required: true
      },
      {
        key: 'value',
        label: 'Valor',
        type: 'text',
        placeholder: '{{response.data}}',
        required: true
      },
      {
        key: 'scope',
        label: 'Escopo',
        type: 'select',
        options: [
          { value: 'flow', label: 'Fluxo' },
          { value: 'session', label: 'Sessão' },
          { value: 'global', label: 'Global' }
        ]
      }
    ]
  },

  // ===== INTEGRAÇÃO =====
  {
    type: 'send_webhook',
    label: 'Enviar Webhook',
    description: 'Envia dados para webhook externo',
    category: 'integration',
    icon: 'fa-share-square',
    defaultConfig: { url: '', method: 'POST', async: true },
    configSchema: [
      {
        key: 'url',
        label: 'URL',
        type: 'text',
        placeholder: 'https://api.example.com/webhook',
        required: true
      },
      {
        key: 'method',
        label: 'Método',
        type: 'select',
        options: [
          { value: 'GET', label: 'GET' },
          { value: 'POST', label: 'POST' },
          { value: 'PUT', label: 'PUT' },
          { value: 'DELETE', label: 'DELETE' }
        ]
      },
      {
        key: 'async',
        label: 'Assíncrono',
        type: 'checkbox'
      }
    ]
  },
  {
    type: 'log_debug',
    label: 'Log de Debug',
    description: 'Registra informações para debug',
    category: 'integration',
    icon: 'fa-bug',
    defaultConfig: { message: '', includeData: true },
    configSchema: [
      {
        key: 'message',
        label: 'Mensagem',
        type: 'text',
        placeholder: 'Debug checkpoint',
        required: true
      },
      {
        key: 'includeData',
        label: 'Incluir Dados do Node',
        type: 'checkbox'
      }
    ]
  },

  // ===== DADOS =====
  {
    type: 'extract_field',
    label: 'Extrair Campo',
    description: 'Extrai campo específico dos dados',
    category: 'data',
    icon: 'fa-filter',
    defaultConfig: { sourcePath: '', targetVariable: '' },
    configSchema: [
      {
        key: 'sourcePath',
        label: 'Caminho do Campo',
        type: 'text',
        placeholder: 'response.data.user.name',
        required: true
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'userName',
        required: true
      },
      {
        key: 'defaultValue',
        label: 'Valor Padrão',
        type: 'text',
        placeholder: 'Valor se não encontrado'
      }
    ]
  },
  {
    type: 'merge_data',
    label: 'Mesclar Dados',
    description: 'Combina múltiplas fontes de dados',
    category: 'data',
    icon: 'fa-object-group',
    defaultConfig: { sources: [], targetVariable: 'merged', strategy: 'deep' },
    configSchema: [
      {
        key: 'sources',
        label: 'Fontes (separadas por vírgula)',
        type: 'array',
        placeholder: 'var1, var2, var3',
        required: true
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'merged',
        required: true
      },
      {
        key: 'strategy',
        label: 'Estratégia',
        type: 'select',
        options: [
          { value: 'shallow', label: 'Superficial' },
          { value: 'deep', label: 'Profunda' },
          { value: 'override', label: 'Sobrescrever' }
        ]
      }
    ]
  },
  {
    type: 'set_default',
    label: 'Definir Valor Padrão',
    description: 'Define valor padrão se campo estiver vazio',
    category: 'data',
    icon: 'fa-equals',
    defaultConfig: { field: '', defaultValue: '' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        placeholder: 'message',
        required: true
      },
      {
        key: 'defaultValue',
        label: 'Valor Padrão',
        type: 'text',
        placeholder: 'Olá!',
        required: true
      }
    ]
  },

  // ===== BANCO DE DADOS =====
  {
    type: 'db_query',
    label: 'Consultar Banco de Dados',
    description: 'Executa query SQL e armazena resultado',
    category: 'database',
    icon: 'fa-database',
    defaultConfig: { query: '', parameters: {}, targetVariable: 'queryResult' },
    configSchema: [
      {
        key: 'query',
        label: 'Query SQL',
        type: 'textarea',
        placeholder: 'SELECT * FROM users WHERE id = @userId',
        required: true
      },
      {
        key: 'parameters',
        label: 'Parâmetros (JSON)',
        type: 'textarea',
        placeholder: '{"userId": "{{user.id}}"}'
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'queryResult',
        required: true
      }
    ]
  },
  {
    type: 'db_insert',
    label: 'Inserir no Banco',
    description: 'Insere registro no banco de dados',
    category: 'database',
    icon: 'fa-plus-square',
    defaultConfig: { table: '', data: {}, returnId: true },
    configSchema: [
      {
        key: 'table',
        label: 'Tabela',
        type: 'text',
        placeholder: 'users',
        required: true
      },
      {
        key: 'data',
        label: 'Dados (JSON)',
        type: 'textarea',
        placeholder: '{"name": "{{name}}", "email": "{{email}}"}',
        required: true
      },
      {
        key: 'returnId',
        label: 'Retornar ID Inserido',
        type: 'checkbox'
      }
    ]
  },
  {
    type: 'db_update',
    label: 'Atualizar no Banco',
    description: 'Atualiza registro existente',
    category: 'database',
    icon: 'fa-edit',
    defaultConfig: { table: '', data: {}, where: {} },
    configSchema: [
      {
        key: 'table',
        label: 'Tabela',
        type: 'text',
        placeholder: 'users',
        required: true
      },
      {
        key: 'data',
        label: 'Dados para Atualizar (JSON)',
        type: 'textarea',
        placeholder: '{"status": "active"}',
        required: true
      },
      {
        key: 'where',
        label: 'Condição WHERE (JSON)',
        type: 'textarea',
        placeholder: '{"id": "{{userId}}"}',
        required: true
      }
    ]
  },
  {
    type: 'db_delete',
    label: 'Deletar do Banco',
    description: 'Remove registro do banco de dados',
    category: 'database',
    icon: 'fa-trash',
    defaultConfig: { table: '', where: {} },
    configSchema: [
      {
        key: 'table',
        label: 'Tabela',
        type: 'text',
        placeholder: 'users',
        required: true
      },
      {
        key: 'where',
        label: 'Condição WHERE (JSON)',
        type: 'textarea',
        placeholder: '{"id": "{{userId}}"}',
        required: true
      }
    ]
  },

  // ===== API =====
  {
    type: 'api_call',
    label: 'Chamada de API',
    description: 'Faz requisição HTTP para API externa',
    category: 'api',
    icon: 'fa-plug',
    defaultConfig: { url: '', method: 'GET', headers: {}, targetVariable: 'apiResponse' },
    configSchema: [
      {
        key: 'url',
        label: 'URL',
        type: 'text',
        placeholder: 'https://api.example.com/endpoint',
        required: true
      },
      {
        key: 'method',
        label: 'Método HTTP',
        type: 'select',
        options: [
          { value: 'GET', label: 'GET' },
          { value: 'POST', label: 'POST' },
          { value: 'PUT', label: 'PUT' },
          { value: 'DELETE', label: 'DELETE' },
          { value: 'PATCH', label: 'PATCH' }
        ],
        required: true
      },
      {
        key: 'headers',
        label: 'Headers (JSON)',
        type: 'textarea',
        placeholder: '{"Content-Type": "application/json"}'
      },
      {
        key: 'body',
        label: 'Body (JSON)',
        type: 'textarea',
        placeholder: '{"key": "value"}'
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'apiResponse',
        required: true
      }
    ]
  },
  {
    type: 'api_auth',
    label: 'Autenticação API',
    description: 'Adiciona autenticação à requisição',
    category: 'api',
    icon: 'fa-key',
    defaultConfig: { type: 'bearer', token: '' },
    configSchema: [
      {
        key: 'type',
        label: 'Tipo de Autenticação',
        type: 'select',
        options: [
          { value: 'bearer', label: 'Bearer Token' },
          { value: 'basic', label: 'Basic Auth' },
          { value: 'apikey', label: 'API Key' }
        ],
        required: true
      },
      {
        key: 'token',
        label: 'Token/Chave',
        type: 'text',
        placeholder: 'seu_token_aqui',
        required: true
      },
      {
        key: 'headerName',
        label: 'Nome do Header (para API Key)',
        type: 'text',
        placeholder: 'X-API-Key'
      }
    ]
  },
  {
    type: 'api_rate_limit',
    label: 'Controle de Rate Limit',
    description: 'Gerencia limite de requisições por tempo',
    category: 'api',
    icon: 'fa-tachometer-alt',
    defaultConfig: { maxRequests: 100, timeWindow: 60, action: 'wait' },
    configSchema: [
      {
        key: 'maxRequests',
        label: 'Máximo de Requisições',
        type: 'number',
        placeholder: '100',
        required: true
      },
      {
        key: 'timeWindow',
        label: 'Janela de Tempo (segundos)',
        type: 'number',
        placeholder: '60',
        required: true
      },
      {
        key: 'action',
        label: 'Ação ao Exceder',
        type: 'select',
        options: [
          { value: 'wait', label: 'Aguardar' },
          { value: 'skip', label: 'Pular' },
          { value: 'error', label: 'Gerar Erro' }
        ]
      }
    ]
  },

  // ===== NOTIFICAÇÕES =====
  {
    type: 'push_notification',
    label: 'Notificação Push',
    description: 'Envia notificação push para dispositivo',
    category: 'notification',
    icon: 'fa-bell',
    defaultConfig: { title: '', body: '', priority: 'normal' },
    configSchema: [
      {
        key: 'title',
        label: 'Título',
        type: 'text',
        placeholder: 'Título da notificação',
        required: true
      },
      {
        key: 'body',
        label: 'Mensagem',
        type: 'textarea',
        placeholder: 'Corpo da notificação',
        required: true
      },
      {
        key: 'userId',
        label: 'ID do Usuário',
        type: 'text',
        placeholder: '{{user.id}}'
      },
      {
        key: 'priority',
        label: 'Prioridade',
        type: 'select',
        options: [
          { value: 'high', label: 'Alta' },
          { value: 'normal', label: 'Normal' },
          { value: 'low', label: 'Baixa' }
        ]
      },
      {
        key: 'data',
        label: 'Dados Extras (JSON)',
        type: 'textarea',
        placeholder: '{"action": "open_screen"}'
      }
    ]
  },
  {
    type: 'sms_notification',
    label: 'Enviar SMS',
    description: 'Envia SMS para número de telefone',
    category: 'notification',
    icon: 'fa-sms',
    defaultConfig: { phone: '', message: '' },
    configSchema: [
      {
        key: 'phone',
        label: 'Número de Telefone',
        type: 'text',
        placeholder: '+5511999999999',
        required: true
      },
      {
        key: 'message',
        label: 'Mensagem',
        type: 'textarea',
        placeholder: 'Seu código de verificação é: {{code}}',
        required: true
      },
      {
        key: 'provider',
        label: 'Provedor',
        type: 'select',
        options: [
          { value: 'twilio', label: 'Twilio' },
          { value: 'nexmo', label: 'Nexmo' },
          { value: 'aws_sns', label: 'AWS SNS' }
        ]
      }
    ]
  },
  {
    type: 'whatsapp_notification',
    label: 'Enviar WhatsApp',
    description: 'Envia mensagem via WhatsApp Business API',
    category: 'notification',
    icon: 'fa-whatsapp',
    defaultConfig: { phone: '', message: '', template: '' },
    configSchema: [
      {
        key: 'phone',
        label: 'Número WhatsApp',
        type: 'text',
        placeholder: '+5511999999999',
        required: true
      },
      {
        key: 'template',
        label: 'Template',
        type: 'text',
        placeholder: 'welcome_message'
      },
      {
        key: 'message',
        label: 'Mensagem',
        type: 'textarea',
        placeholder: 'Olá {{name}}, bem-vindo!',
        required: true
      },
      {
        key: 'mediaUrl',
        label: 'URL de Mídia (opcional)',
        type: 'text',
        placeholder: 'https://example.com/image.jpg'
      }
    ]
  },

  // ===== SEGURANÇA =====
  {
    type: 'encrypt_data',
    label: 'Criptografar Dados',
    description: 'Criptografa dados sensíveis',
    category: 'security',
    icon: 'fa-lock',
    defaultConfig: { field: '', algorithm: 'AES-256', targetVariable: 'encrypted' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo para Criptografar',
        type: 'text',
        placeholder: 'password',
        required: true
      },
      {
        key: 'algorithm',
        label: 'Algoritmo',
        type: 'select',
        options: [
          { value: 'AES-256', label: 'AES-256' },
          { value: 'AES-128', label: 'AES-128' },
          { value: 'RSA', label: 'RSA' }
        ]
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'encrypted',
        required: true
      }
    ]
  },
  {
    type: 'decrypt_data',
    label: 'Descriptografar Dados',
    description: 'Descriptografa dados criptografados',
    category: 'security',
    icon: 'fa-unlock',
    defaultConfig: { field: '', algorithm: 'AES-256', targetVariable: 'decrypted' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo para Descriptografar',
        type: 'text',
        placeholder: 'encryptedData',
        required: true
      },
      {
        key: 'algorithm',
        label: 'Algoritmo',
        type: 'select',
        options: [
          { value: 'AES-256', label: 'AES-256' },
          { value: 'AES-128', label: 'AES-128' },
          { value: 'RSA', label: 'RSA' }
        ]
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'decrypted',
        required: true
      }
    ]
  },
  {
    type: 'hash_password',
    label: 'Hash de Senha',
    description: 'Gera hash seguro de senha',
    category: 'security',
    icon: 'fa-shield-alt',
    defaultConfig: { field: 'password', algorithm: 'bcrypt', targetVariable: 'hashedPassword' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo da Senha',
        type: 'text',
        placeholder: 'password',
        required: true
      },
      {
        key: 'algorithm',
        label: 'Algoritmo',
        type: 'select',
        options: [
          { value: 'bcrypt', label: 'BCrypt' },
          { value: 'argon2', label: 'Argon2' },
          { value: 'sha256', label: 'SHA-256' }
        ]
      },
      {
        key: 'rounds',
        label: 'Rounds/Custo',
        type: 'number',
        placeholder: '10'
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'hashedPassword',
        required: true
      }
    ]
  },
  {
    type: 'verify_signature',
    label: 'Verificar Assinatura',
    description: 'Valida assinatura digital de dados',
    category: 'security',
    icon: 'fa-certificate',
    defaultConfig: { data: '', signature: '', publicKey: '' },
    configSchema: [
      {
        key: 'data',
        label: 'Dados',
        type: 'textarea',
        placeholder: '{{payload}}',
        required: true
      },
      {
        key: 'signature',
        label: 'Assinatura',
        type: 'textarea',
        placeholder: '{{signature}}',
        required: true
      },
      {
        key: 'publicKey',
        label: 'Chave Pública',
        type: 'textarea',
        placeholder: 'Chave pública RSA',
        required: true
      },
      {
        key: 'algorithm',
        label: 'Algoritmo',
        type: 'select',
        options: [
          { value: 'RSA-SHA256', label: 'RSA-SHA256' },
          { value: 'ECDSA', label: 'ECDSA' },
          { value: 'HMAC', label: 'HMAC' }
        ]
      }
    ]
  },

  // ===== TRANSFORMAÇÕES ADICIONAIS =====
  {
    type: 'transform_remove_html',
    label: 'Remover HTML',
    description: 'Remove tags HTML do texto',
    category: 'transformation',
    icon: 'fa-code',
    defaultConfig: { field: 'message', keepLineBreaks: true },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        placeholder: 'message',
        required: true
      },
      {
        key: 'keepLineBreaks',
        label: 'Manter Quebras de Linha',
        type: 'checkbox'
      }
    ]
  },
  {
    type: 'transform_slugify',
    label: 'Criar Slug',
    description: 'Converte texto em slug (URL-friendly)',
    category: 'transformation',
    icon: 'fa-link',
    defaultConfig: { field: 'title', targetVariable: 'slug' },
    configSchema: [
      {
        key: 'field',
        label: 'Campo',
        type: 'text',
        placeholder: 'title',
        required: true
      },
      {
        key: 'targetVariable',
        label: 'Variável de Destino',
        type: 'text',
        placeholder: 'slug',
        required: true
      },
      {
        key: 'lowercase',
        label: 'Forçar Minúsculas',
        type: 'checkbox'
      }
    ]
  }
];

// Helper para obter definição de ação por tipo
export const getActionDefinition = (type: ActionType): ActionDefinition | undefined => {
  return ACTION_DEFINITIONS.find(def => def.type === type);
};

// Helper para criar nova ação com valores padrão
export const createNewAction = (type: ActionType, isInput: boolean): InputAction | OutputAction => {
  const definition = getActionDefinition(type);
  if (!definition) {
    throw new Error(`Action type ${type} not found`);
  }

  const baseAction: NodeAction = {
    id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    label: definition.label,
    enabled: true,
    config: { ...definition.defaultConfig }
  };

  if (isInput) {
    return { ...baseAction, order: 0 } as InputAction;
  } else {
    return { ...baseAction, order: 0 } as OutputAction;
  }
};
